import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { createContext } from 'react';

import { activeVouchersBunja } from '@flipster/features/src/reward-hub-page/v2/vouchers/state';
import { getMarketDiscountState } from '@flipster/features/src/shared/fee-discount/market-discount';
import { snapshotSpecBunja } from '@flipster/features/src/shared/snapshot/state';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import { pswapDetailsBunja } from '@flipster/r230522/original/pswap-detail';
import { pswapsBunja } from '@flipster/r230522/original/pswaps';
import { tradeSymbolAtom } from '@flipster/r230522/original/trade-symbol';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { calcEstLiquidationPriceWithFee, calcQtyToMargin } from '@flipster/utils/calculator/order-preview';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import Num, { BigNumber, countDecimalPlaces } from '@flipster/utils/num';

import { maxPositionLimit } from '@web/constants/position';

import { hedgingOrderLeverage, hedgingOrderSide } from '../constants';

export interface HedgingFormContext {
  stakingCurrency: string;
  stakedAmount: string;
}

export const HedgingFormContext = createContext<HedgingFormContext>(null!);
export const HedgingFormScope = createScopeFromContext(HedgingFormContext);

export const hedgingFormBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { stakingCurrency, stakedAmount } = bunja.use(HedgingFormScope);
  const { snapshotSpecAtom } = bunja.use(snapshotSpecBunja);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { pswapsAtom } = bunja.use(pswapsBunja);
  const { pswapDetailsAtom, setCurrentSymbol } = bunja.use(pswapDetailsBunja);
  const { perpFeeVoucherAvailableAmountAtom } = bunja.use(activeVouchersBunja);

  const pswapInstrumentsAtom = atom((get) => get(snapshotSpecAtom)?.pswapInstruments);

  const symbolInfoAtom = atom((get) =>
    get(pswapInstrumentsAtom)?.find((pi) => pi.quoteCurrency === UsdtCurrency && pi.currency === stakingCurrency),
  );

  const pswapDetailAtom = atom((get) => {
    const symbolInfo = get(symbolInfoAtom);
    if (!symbolInfo) return null;
    return get(pswapDetailsAtom)?.[symbolInfo.symbol];
  });

  const shortOpenPositionsAtom = atom((get) => {
    const openPositions = get(openPositionsAtom);
    const symbolInfo = get(symbolInfoAtom);
    if (!symbolInfo) return '0';
    const { symbol } = symbolInfo;
    const allShortPositions = openPositions?.[symbol]?.filter((op) => op.side === 'Short') ?? [];
    return (
      allShortPositions
        ?.reduce((acc, curr) => acc.plus(Num(curr.position).abs()), Num(0))
        .abs()
        .toString() ?? '0'
    );
  });

  const orderPriceAtom = atom((get) => {
    const shortOpenPositions = get(shortOpenPositionsAtom);
    const symbolInfo = get(symbolInfoAtom);
    if (!symbolInfo) return '0';
    const { unitOrderQty } = symbolInfo;
    return Num(stakedAmount)
      .minus(shortOpenPositions)
      .toFixed(countDecimalPlaces(unitOrderQty), BigNumber.ROUND_HALF_UP)
      .toString();
  });

  const marginAtom = atom((get) => {
    const symbolInfo = get(symbolInfoAtom);
    if (!symbolInfo) return '0';
    const pswapDetail = get(pswapDetailsAtom)?.[symbolInfo?.symbol];
    return calcQtyToMargin(get(orderPriceAtom), hedgingOrderLeverage, pswapDetail?.askPrice);
  });

  const liqShortPriceAtom = atom((get) => {
    const symbolInfo = get(symbolInfoAtom);
    if (!symbolInfo) return '0';
    const { maintMarginRate, tickSize } = symbolInfo;
    const pswapDetail = get(pswapDetailsAtom)?.[symbolInfo?.symbol];
    const pswap = get(pswapsAtom)?.[symbolInfo.symbol];

    return calcEstLiquidationPriceWithFee(
      hedgingOrderSide,
      hedgingOrderLeverage,
      pswapDetail?.bidPrice ?? '0',
      maintMarginRate,
      pswap?.takerFeeRate ?? '0',
      pswap?.makerFeeRate ?? '0',
      tickSize,
    );
  });

  const takerFeeRateAtom = atom((get) => {
    const symbolInfo = get(symbolInfoAtom);
    if (!symbolInfo) return '0';
    return get(pswapsAtom)?.[symbolInfo.symbol]?.takerFeeRate ?? '0';
  });

  const estimatedTradingFeeRatePercentAtom = atom((get) => {
    const takerFeeRate = get(takerFeeRateAtom);
    return Num(takerFeeRate).multipliedBy(100).toString();
  });

  const estimatedTradingFeeAtom = atom((get) => {
    const orderPrice = get(orderPriceAtom);
    const pswapDetail = get(pswapDetailAtom);
    const takerFeeRate = get(takerFeeRateAtom);
    const positionSizeInUsdt = Num(orderPrice).multipliedBy(pswapDetail?.bidPrice ?? '0');
    return positionSizeInUsdt.multipliedBy(takerFeeRate).toFixed(UsdtPrecision, BigNumber.ROUND_UP);
  });

  const feeDiscountStateAtom = atom((get) => {
    const estTradingFee = Num(get(estimatedTradingFeeAtom));
    const voucher = get(perpFeeVoucherAvailableAmountAtom);
    return getMarketDiscountState(estTradingFee, voucher);
  });

  const isFullDiscountAtom = atom((get) => {
    const state = get(feeDiscountStateAtom);
    return state?.displayFeeRatePercent && state.displayFeeRatePercent.isZero();
  });

  const isHedgeOverStakingAmountAtom = atom((get) => {
    return Num(get(orderPriceAtom)).isLessThanOrEqualTo(0);
  });

  const isOverMaxPositionsAtom = atom((get) => {
    const symbolInfo = get(symbolInfoAtom);
    if (!symbolInfo) return false;
    const { symbol } = symbolInfo;
    const openPositions = get(openPositionsAtom);
    return openPositions?.[symbol] && openPositions[symbol].length >= maxPositionLimit;
  });

  bunja.effect(() => {
    setSubscriptionState();
    // unmount시 setCurrentSymbol(null)을 하면 동시성 이슈가 발생할 수 있음
    // 어차피 pswapDetailsBunja를 의존하지 않으면 구독이 알아서 끊길 것이므로 unmount시 null set은 하지 않음
    // FIXME: 추후 일반구독 구조를 적용하면서 개선 필요
    return store.sub(symbolInfoAtom, setSubscriptionState);
    function setSubscriptionState() {
      const symbolInfo = store.get(symbolInfoAtom);
      if (!symbolInfo) return;
      setCurrentSymbol(symbolInfo.symbol);
      store.set(tradeSymbolAtom, symbolInfo.symbol); // FIXME: set 대신 scope 등으로부터 derive 되는 구조로 만들어야 함
    }
  });

  return {
    symbolInfoAtom,
    pswapDetailAtom,
    shortOpenPositionsAtom,
    orderPriceAtom,
    marginAtom,
    liqShortPriceAtom,
    takerFeeRateAtom,
    estimatedTradingFeeRatePercentAtom,
    feeDiscountStateAtom,
    isFullDiscountAtom,
    isHedgeOverStakingAmountAtom,
    isOverMaxPositionsAtom,
  };
});
hedgingFormBunja.debugLabel = 'hedgingFormBunja';
